import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterIcon, DownloadIcon, PlusIcon, UserIcon, BuildingIcon, ChevronDownIcon, CheckIcon, ChevronUpIcon, ArrowRightIcon } from 'lucide-react';
type SortField = 'company' | 'industry' | 'size' | 'location' | 'revenue' | 'score';
type SortDirection = 'asc' | 'desc';
export const LeadScout = () => {
  const navigate = useNavigate();
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedRevenues, setSelectedRevenues] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRevenueDropdownOpen, setIsRevenueDropdownOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('score');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  // Sample data for demonstration
  const leads = [{
    id: 1,
    companyName: 'Tech Innovate GmbH',
    industry: 'IT-Dienstleistungen',
    size: '50-100',
    location: 'München',
    revenue: '€5M - €10M',
    score: 92,
    contactCount: 2
  }, {
    id: 2,
    companyName: 'Digital Solutions AG',
    industry: 'Software-Entwicklung',
    size: '100-250',
    location: 'Berlin',
    revenue: '€10M - €25M',
    score: 87,
    contactCount: 3
  }, {
    id: 3,
    companyName: 'Future Systems GmbH',
    industry: 'IT-Beratung',
    size: '25-50',
    location: 'Hamburg',
    revenue: '€2.5M - €5M',
    score: 85,
    contactCount: 1
  }, {
    id: 4,
    companyName: 'Quantum Innovations GmbH',
    industry: 'Hardware',
    size: '250-500',
    location: 'Frankfurt',
    revenue: '€25M - €50M',
    score: 81,
    contactCount: 4
  }, {
    id: 5,
    companyName: 'SmartTech Solutions',
    industry: 'IT-Sicherheit',
    size: '10-25',
    location: 'Köln',
    revenue: '€1M - €2.5M',
    score: 78,
    contactCount: 2
  }];
  const filters = [{
    name: 'Branche',
    options: ['IT-Dienstleistungen', 'Software-Entwicklung', 'IT-Beratung', 'Hardware', 'IT-Sicherheit']
  }, {
    name: 'Unternehmensgröße',
    options: ['1-10', '10-25', '25-50', '50-100', '100-250', '250-500', '500+']
  }, {
    name: 'Standort',
    options: ['München', 'Berlin', 'Hamburg', 'Frankfurt', 'Köln', 'Stuttgart']
  }, {
    name: 'Umsatz',
    options: ['€0 - €1M', '€1M - €2.5M', '€2.5M - €5M', '€5M - €10M', '€10M - €25M', '€25M+']
  }];
  // Sort function
  const sortData = (a: any, b: any) => {
    let compareA = sortField === 'company' ? a.companyName : a[sortField];
    let compareB = sortField === 'company' ? b.companyName : b[sortField];
    // Handle numeric values in strings (e.g., "50-100")
    if (sortField === 'size') {
      compareA = parseInt(compareA.split('-')[0]);
      compareB = parseInt(compareB.split('-')[0]);
    }
    // Handle revenue ranges
    if (sortField === 'revenue') {
      const getRevenueValue = (rev: string) => {
        const value = parseInt(rev.match(/\d+/)?.[0] || '0');
        if (rev.includes('M')) return value * 1000000;
        return value * 1000000; // Default to millions
      };
      compareA = getRevenueValue(compareA);
      compareB = getRevenueValue(compareB);
    }
    if (compareA < compareB) return sortDirection === 'asc' ? -1 : 1;
    if (compareA > compareB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  };
  // Filter and sort leads
  const filteredAndSortedLeads = leads.filter(lead => (selectedSizes.length === 0 ? true : selectedSizes.includes(lead.size)) && (selectedRevenues.length === 0 ? true : selectedRevenues.includes(lead.revenue))).sort(sortData);
  // Handle column header click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon size={16} className="ml-1" data-id="element-190" /> : <ChevronDownIcon size={16} className="ml-1" data-id="element-191" />;
  };
  return <div className="space-y-6" data-id="element-192">
      <div className="flex items-center justify-between" data-id="element-193">
        <h2 className="text-xl font-semibold text-gray-800" data-id="element-194">Lead-Suche</h2>
        <div className="flex items-center space-x-3" data-id="element-195">
          <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 flex items-center hover:bg-gray-50" data-id="element-196">
            <FilterIcon size={16} className="mr-2 text-gray-500" data-id="element-197" />
            Filter
          </button>
          <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 flex items-center hover:bg-gray-50" data-id="element-198">
            <DownloadIcon size={16} className="mr-2 text-gray-500" data-id="element-199" />
            Exportieren
          </button>
          <button className="px-4 py-2 bg-[#17B978] rounded-lg text-sm font-medium text-white flex items-center hover:bg-[#148F5F]" data-id="element-200">
            <PlusIcon size={16} className="mr-2" data-id="element-201" />
            Neue Suche
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-202">
        <div className="p-5 border-b border-gray-200" data-id="element-203">
          <h3 className="text-lg font-medium text-gray-800" data-id="element-204">
            Ideal Customer Profile
          </h3>
          <p className="text-sm text-gray-500 mt-1" data-id="element-205">
            Definieren Sie Ihre Zielkunden basierend auf firmografischen und
            verhaltensbasierten Daten
          </p>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-gray-50" data-id="element-206">
          {filters.map((filter, index) => <div key={index} className="space-y-2" data-id="element-207">
              <label className="text-sm font-medium text-gray-700" data-id="element-208">
                {filter.name}
              </label>
              {filter.name === 'Unternehmensgröße' ? <div className="relative" data-id="element-209">
                  <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-210">
                    <div className="flex justify-between items-center" data-id="element-211">
                      <span data-id="element-212">
                        {selectedSizes.length === 0 ? 'Alle auswählen' : `${selectedSizes.length} ausgewählt`}
                      </span>
                      <ChevronDownIcon size={16} className="text-gray-500" data-id="element-213" />
                    </div>
                  </button>
                  {isDropdownOpen && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg" data-id="element-214">
                      <div className="p-2 space-y-1 max-h-48 overflow-auto" data-id="element-215">
                        {filter.options.map((option, i) => <div key={i} className="flex items-center px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer" onClick={() => {
                  const newSelected = selectedSizes.includes(option) ? selectedSizes.filter(size => size !== option) : [...selectedSizes, option];
                  setSelectedSizes(newSelected);
                }} data-id="element-216">
                            <div className="w-4 h-4 border border-gray-300 rounded mr-2 flex items-center justify-center" data-id="element-217">
                              {selectedSizes.includes(option) && <CheckIcon size={12} className="text-blue-500" data-id="element-218" />}
                            </div>
                            <span className="text-sm text-gray-700" data-id="element-219">
                              {option}
                            </span>
                          </div>)}
                      </div>
                    </div>}
                </div> : filter.name === 'Umsatz' ? <div className="relative" data-id="element-220">
                  <button onClick={() => setIsRevenueDropdownOpen(!isRevenueDropdownOpen)} className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-left text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-221">
                    <div className="flex justify-between items-center" data-id="element-222">
                      <span data-id="element-223">
                        {selectedRevenues.length === 0 ? 'Alle auswählen' : `${selectedRevenues.length} ausgewählt`}
                      </span>
                      <ChevronDownIcon size={16} className="text-gray-500" data-id="element-224" />
                    </div>
                  </button>
                  {isRevenueDropdownOpen && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg" data-id="element-225">
                      <div className="p-2 space-y-1 max-h-48 overflow-auto" data-id="element-226">
                        {filter.options.map((option, i) => <div key={i} className="flex items-center px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer" onClick={() => {
                  const newSelected = selectedRevenues.includes(option) ? selectedRevenues.filter(revenue => revenue !== option) : [...selectedRevenues, option];
                  setSelectedRevenues(newSelected);
                }} data-id="element-227">
                            <div className="w-4 h-4 border border-gray-300 rounded mr-2 flex items-center justify-center" data-id="element-228">
                              {selectedRevenues.includes(option) && <CheckIcon size={12} className="text-blue-500" data-id="element-229" />}
                            </div>
                            <span className="text-sm text-gray-700" data-id="element-230">
                              {option}
                            </span>
                          </div>)}
                      </div>
                    </div>}
                </div> : <div className="relative" data-id="element-231">
                  <select className="appearance-none w-full bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-232">
                    <option value="" data-id="element-233">Alle auswählen</option>
                    {filter.options.map((option, i) => <option key={i} value={option} data-id="element-234">
                        {option}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-235">
                    <ChevronDownIcon size={16} className="text-gray-500" data-id="element-236" />
                  </div>
                </div>}
            </div>)}
        </div>
        <div className="overflow-x-auto" data-id="element-237">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-238">
            <thead className="bg-gray-50" data-id="element-239">
              <tr data-id="element-240">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('company')} data-id="element-241">
                  <div className="flex items-center" data-id="element-242">
                    Unternehmen
                    {renderSortIndicator('company')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('industry')} data-id="element-243">
                  <div className="flex items-center" data-id="element-244">
                    Branche
                    {renderSortIndicator('industry')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('size')} data-id="element-245">
                  <div className="flex items-center" data-id="element-246">
                    Größe
                    {renderSortIndicator('size')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('location')} data-id="element-247">
                  <div className="flex items-center" data-id="element-248">
                    Standort
                    {renderSortIndicator('location')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('revenue')} data-id="element-249">
                  <div className="flex items-center" data-id="element-250">
                    Umsatz
                    {renderSortIndicator('revenue')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('score')} data-id="element-251">
                  <div className="flex items-center" data-id="element-252">
                    Lead Score
                    {renderSortIndicator('score')}
                  </div>
                </th>
                <th scope="col" className="relative px-6 py-3" data-id="element-253">
                  <span className="sr-only" data-id="element-254">Aktionen</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-255">
              {filteredAndSortedLeads.map(lead => <tr key={lead.id} className="hover:bg-gray-50" data-id="element-256">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-257">
                    <div className="flex items-center" data-id="element-258">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-259">
                        <BuildingIcon size={18} className="text-blue-500" data-id="element-260" />
                      </div>
                      <div className="ml-4" data-id="element-261">
                        <div className="text-sm font-medium text-gray-900" data-id="element-262">
                          {lead.companyName}
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-263">
                          <UserIcon size={12} className="mr-1" data-id="element-264" />
                          <span data-id="element-265">{lead.contactCount} Kontakte</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-266">
                    <div className="text-sm text-gray-900" data-id="element-267">{lead.industry}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-268">
                    <div className="text-sm text-gray-900" data-id="element-269">
                      {lead.size} Mitarbeiter
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-270">
                    <div className="text-sm text-gray-900" data-id="element-271">{lead.location}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-272">
                    <div className="text-sm text-gray-900" data-id="element-273">{lead.revenue}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-274">
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-275">
                      {lead.score}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-276">
                    <button onClick={() => navigate(`/leadscout/${lead.id}`)} className="text-[#4F8EF7] hover:text-[#2E6DD9] flex items-center justify-end" data-id="element-277">
                      Details
                      <ArrowRightIcon size={14} className="ml-1" data-id="element-278" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200" data-id="element-279">
          <div className="text-sm text-gray-700" data-id="element-280">
            Zeige <span className="font-medium" data-id="element-281">1</span> bis{' '}
            <span className="font-medium" data-id="element-282">5</span> von{' '}
            <span className="font-medium" data-id="element-283">42</span> Ergebnissen
          </div>
          <div className="flex items-center space-x-2" data-id="element-284">
            <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-285">
              Zurück
            </button>
            <button className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-286">
              Weiter
            </button>
          </div>
        </div>
      </div>
    </div>;
};